export type Selection = { start: number; end: number };

export type EditorAction =
  | { type: "bold" | "italic" | "underline" | "strikethrough" }
  | { type: "heading"; level: 0 | 1 | 2 | 3 | 4 | 5 | 6 }
  | { type: "bulletList" | "numberedList" | "clear" | "table" }
  | { type: "link"; href: string };

export type EditorSnapshot = { value: string; selection: Selection };
export type HistoryState = { past: EditorSnapshot[]; future: EditorSnapshot[]; limit: number };

export const createHistory = (limit = 100): HistoryState => ({
  past: [],
  future: [],
  limit,
});

export function pushHistory(history: HistoryState, snapshot: EditorSnapshot): HistoryState {
  if (history.past.at(-1)?.value === snapshot.value && history.past.at(-1)?.selection.start === snapshot.selection.start && history.past.at(-1)?.selection.end === snapshot.selection.end) return history;
  return { ...history, past: [...history.past, snapshot].slice(-history.limit), future: [] };
}

export function undoHistory(history: HistoryState, current: EditorSnapshot) {
  const snapshot = history.past.at(-1);
  if (snapshot === undefined) return { history, snapshot: current };
  return {
    snapshot,
    history: {
      ...history,
      past: history.past.slice(0, -1),
      future: [current, ...history.future].slice(0, history.limit),
    },
  };
}

export function redoHistory(history: HistoryState, current: EditorSnapshot) {
  const snapshot = history.future[0];
  if (snapshot === undefined) return { history, snapshot: current };
  return {
    snapshot,
    history: {
      ...history,
      past: [...history.past, current].slice(-history.limit),
      future: history.future.slice(1),
    },
  };
}

const lineStart = (value: string, index: number) => value.lastIndexOf("\n", Math.max(0, index - 1)) + 1;
const lineEnd = (value: string, index: number) => {
  const end = value.indexOf("\n", index);
  return end === -1 ? value.length : end;
};

function replaceSelection(value: string, selection: Selection, before: string, after = before, placeholder = "text") {
  const selected = value.slice(selection.start, selection.end) || placeholder;
  const replacement = `${before}${selected}${after}`;
  const start = selection.start + before.length;
  return { value: `${value.slice(0, selection.start)}${replacement}${value.slice(selection.end)}`, selection: { start, end: start + selected.length } };
}

function transformLines(value: string, selection: Selection, transform: (line: string, index: number) => string) {
  const start = lineStart(value, selection.start);
  const end = lineEnd(value, selection.end);
  const lines = value.slice(start, end).split("\n").map(transform);
  const replacement = lines.join("\n");
  return { value: `${value.slice(0, start)}${replacement}${value.slice(end)}`, selection: { start, end: start + replacement.length } };
}

export function applyEditorAction(value: string, selection: Selection, action: EditorAction) {
  switch (action.type) {
    case "bold": return replaceSelection(value, selection, "**");
    case "italic": return replaceSelection(value, selection, "*", "*");
    case "underline": return replaceSelection(value, selection, "<u>", "</u>");
    case "strikethrough": return replaceSelection(value, selection, "~~", "~~");
    case "link": return replaceSelection(value, selection, "[", `](${action.href})`, "link text");
    case "heading":
      return transformLines(value, selection, (line) => line.replace(/^\s{0,3}#{1,6}\s+/, "").replace(/^\s*$/, action.level === 0 ? "" : `${"#".repeat(action.level)} `));
    case "bulletList":
      return transformLines(value, selection, (line) => /^\s*[-*+]\s+/.test(line) ? line.replace(/^\s*[-*+]\s+/, "") : `- ${line}`);
    case "numberedList":
      return transformLines(value, selection, (line, index) => /^\s*\d+[.)]\s+/.test(line) ? line.replace(/^\s*\d+[.)]\s+/, "") : `${index + 1}. ${line}`);
    case "table": {
      const table = "|  |  |\n| --- | --- |\n|  |  |\n|  |  |";
      return { value: `${value.slice(0, selection.start)}${table}${value.slice(selection.end)}`, selection: { start: selection.start + table.length, end: selection.start + table.length } };
    }
    case "clear": {
      const selected = value.slice(selection.start, selection.end) || value;
      const cleared = selected
        .replace(/<u>(.*?)<\/u>/gis, "$1")
        .replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1")
        .replace(/(^|\n)\s{0,3}#{1,6}\s+/g, "$1")
        .replace(/(^|\n)\s*(?:[-*+]\s+|\d+[.)]\s+)/g, "$1")
        .replace(/(\*\*|__|~~|\*|_)/g, "");
      const nextValue = selected === value ? cleared : `${value.slice(0, selection.start)}${cleared}${value.slice(selection.end)}`;
      return { value: nextValue, selection: selected === value ? { start: 0, end: cleared.length } : { start: selection.start, end: selection.start + cleared.length } };
    }
  }
}

export function isSafeHref(href: string): boolean {
  if (!href || /[\u0000-\u001f\u007f-\u009f\u200b-\u200d\ufeff\\]/.test(href)) return false;
  const value = href.trim();
  if (!value || value.startsWith("//")) return false;
  const scheme = value.match(/^([^/?#\s:]+):/i)?.[1]?.toLowerCase();
  if (scheme) return scheme === "http" || scheme === "https" || scheme === "mailto";
  if (value.includes(":") && !value.startsWith("/") && !value.startsWith("#")) return false;
  return value.startsWith("/") || value.startsWith("#");
}

export const isExternalHttpHref = (href: string) => /^https?:\/\//i.test(href) && isSafeHref(href);
